import { useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { messageApi, type ChatMessage, type ChatConversation, type SendChatParams } from '@/services/api';

export function useMessages() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ChatConversation[]>([]);

  const loadConversations = useCallback(async () => {
    if (!user) { setConversations([]); return []; }
    try {
      const convs = await messageApi.conversations();
      setConversations(convs);
      return convs;
    } catch {
      return [];
    }
  }, [user]);

  const loadMessages = useCallback(async (peerId: number): Promise<ChatMessage[]> => {
    if (!user) return [];
    try {
      return await messageApi.messages(peerId);
    } catch {
      return [];
    }
  }, [user]);

  const sendMessage = useCallback(async (peerId: number, body: SendChatParams): Promise<ChatMessage | null> => {
    if (!user) return null;
    try {
      return await messageApi.send(peerId, body);
    } catch {
      return null;
    }
  }, [user]);

  const markConversationRead = useCallback(async (peerId: number) => {
    if (!user) return;
    try {
      await messageApi.markRead(peerId);
      setConversations((prev) => prev.map((c) => (c.peerId === peerId ? { ...c, unreadCount: 0 } : c)));
    } catch { /* ignore */ }
  }, [user]);

  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return {
    conversations,
    totalUnread,
    loadConversations,
    loadMessages,
    sendMessage,
    markConversationRead,
  };
}
